// Phase 12.2 — luminance-aware label color picker.
// Used for chart labels that sit ON a colored fill (donut segment, bar value
// label, avatar pill, etc.) so the label remains AA-readable regardless of
// the segment color or the active theme.
//
// Inputs accepted:
//   • #rgb / #rrggbb hex strings
//   • rgb()/rgba() css strings
// Anything else falls back to a safe `var(--page-fg)`.
//
// Implementation uses the WCAG relative-luminance formula directly so we don't
// need to pull in `polished` (keeps bundle lean — see "no new dependencies"
// in the Phase 12.1 brief).

function _parseColor(input) {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim();

  // #rgb or #rrggbb
  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (hex.length !== 6) return null;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return [r, g, b];
  }

  // rgb(R, G, B) / rgba(R, G, B, A)
  const m = trimmed.match(
    /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,]+[\d.]+)?\s*\)$/i
  );
  if (m) {
    return [Number(m[1]), Number(m[2]), Number(m[3])];
  }

  return null;
}

function _channelLuminance(c) {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(color) {
  const rgb = _parseColor(color);
  if (!rgb) return 0.5;
  const [r, g, b] = rgb;
  return (
    0.2126 * _channelLuminance(r) +
    0.7152 * _channelLuminance(g) +
    0.0722 * _channelLuminance(b)
  );
}

// Pick a foreground for text laid directly on `bgColor`.
//
// We compute the WCAG contrast ratio for both candidates (near-black vs pure
// white) and return whichever has the higher ratio. This is more reliable than
// a flat luminance threshold because mid-tone fills like our brand gold
// (#D6B16A, luminance 0.47) actually need black text — a naive `> 0.5` check
// would incorrectly hand them white text and silently fail AA.
//
//   light bg  → near-black (#0B0B0F — also our `--page-fg` light value)
//   dark bg   → pure white
const NEAR_BLACK = "#0B0B0F";
const WHITE = "#FFFFFF";

function _contrastRatio(lum1, lum2) {
  const a = Math.max(lum1, lum2);
  const b = Math.min(lum1, lum2);
  return (a + 0.05) / (b + 0.05);
}

export function pickLabelColor(bgColor) {
  const bgLum = relativeLuminance(bgColor);
  const blackLum = relativeLuminance(NEAR_BLACK); // ~0.0034
  const whiteLum = 1; // pure white
  const blackRatio = _contrastRatio(bgLum, blackLum);
  const whiteRatio = _contrastRatio(bgLum, whiteLum);
  return blackRatio >= whiteRatio ? NEAR_BLACK : WHITE;
}

export default pickLabelColor;
